import { useI18nContext } from '@/i18n/i18n-react';
import {
  Check,
  ChevronDown,
  Loader2,
  RotateCcw,
  FunnelX,
  X,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import { useTenantSentCampaigns } from '@/hooks/use-campaigns';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { WithTooltip } from '@/components/with-tooltip';
import { PeriodFilter } from '@/components/period/period-filter';
import { useSalesPredefinedDateRanges } from '@/components/sales-history/use-sales-history-filters';

export type SalesCampaignFilterItem = Pick<CampaignDto, 'id' | 'name'>;

interface SalesFiltersProps {
  textFilter: string;
  setTextFilter: (value: string) => void;
  fromDateFilter: string | undefined;
  setFromDateFilter: (value: string) => void;
  toDateFilter: string | undefined;
  setToDateFilter: (value: string) => void;
  predefinedDateRange: string | undefined;
  setPredefinedDateRange: (value: string | undefined) => void;
  paymentStatusFilter: { value: string; label: string }[];
  setPaymentStatusFilter: React.Dispatch<
    React.SetStateAction<
      {
        value: string;
        label: string;
      }[]
    >
  >;
  campaignsFilter: SalesCampaignFilterItem[];
  setCampaignsFilter: React.Dispatch<
    React.SetStateAction<SalesCampaignFilterItem[]>
  >;
  clearFilters: () => void;
  isLoading: boolean;
  refetch: () => void;
}

export function SalesFilters({
  textFilter,
  setTextFilter,
  fromDateFilter,
  setFromDateFilter,
  toDateFilter,
  setToDateFilter,
  predefinedDateRange,
  setPredefinedDateRange,
  paymentStatusFilter,
  setPaymentStatusFilter,
  campaignsFilter,
  setCampaignsFilter,
  clearFilters,
  isLoading,
  refetch,
}: SalesFiltersProps) {
  const { LL } = useI18nContext();

  const situations = [
    { value: 'paid', label: LL.salesPage.filters.paid() },
    { value: 'unpaid', label: LL.salesPage.filters.unpaid() },
  ];

  const predefinedDateRanges = useSalesPredefinedDateRanges();

  const { data: campaigns, isLoading: isCampaignsLoading } =
    useTenantSentCampaigns();

  return (
    <div className="flex flex-row justify-start gap-2 items-start">
      <div
        className={cn(
          'flex flex-wrap flex-row gap-2 items-center w-full max-md:min-w-0'
        )}
      >
        <div className="relative flex-1 flex-row min-w-[120px] basis-0">
          <div className="absolute top-0 left-0 flex items-center h-full pl-2">
            <div className="h-min aspect-square items-center justify-center rounded-full">
              <Search width={20} height={20} />
            </div>
          </div>
          <Input
            placeholder={LL.salesPage.filters.search()}
            value={textFilter}
            onChange={(event) => setTextFilter(event.target.value)}
            className="pl-8 "
          />
          <div className="absolute top-0 right-0 flex items-center h-full pr-2">
            <WithTooltip tooltip={LL.salesPage.filters.cleanSearch()}>
              <Button
                className="p-1 w-6 h-6 aspect-square items-center justify-center border-border rounded-full"
                onClick={() => setTextFilter('')}
                variant="outline"
              >
                <X />
              </Button>
            </WithTooltip>
          </div>
        </div>

        <PeriodFilter
          fromDate={fromDateFilter}
          setFromDate={setFromDateFilter}
          toDate={toDateFilter}
          setToDate={setToDateFilter}
          predefinedDateRange={predefinedDateRange}
          setPredefinedDateRange={setPredefinedDateRange}
          predefinedDateRanges={predefinedDateRanges}
          defaultDateRange="since_beginning"
        />

        <Popover>
          <PopoverTrigger asChild>
            <Button
              className="justify-between hidden sm:flex"
              variant="outline"
            >
              <>
                <p className="text-ellipsis overflow-hidden whitespace-nowrap">
                  {paymentStatusFilter.length > 0
                    ? paymentStatusFilter.map((s) => s.label).join(', ')
                    : LL.salesPage.filters.situation()}
                </p>
                <ChevronDown />
              </>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0">
            <Command>
              <CommandList>
                <CommandGroup>
                  {situations?.map((sit) => (
                    <CommandItem
                      className="cursor-pointer"
                      onSelect={() => {
                        setPaymentStatusFilter((prev) => {
                          const exists = prev.some(
                            (s) => s.value === sit.value
                          );
                          if (exists)
                            return prev.filter((s) => s.value !== sit.value);

                          return [...prev, sit];
                        });
                      }}
                    >
                      {sit.label}
                      <Check
                        className={cn(
                          'ml-auto',
                          paymentStatusFilter.find(
                            (s) => s.value === sit.value
                          ) !== undefined
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              className="justify-between max-w-[180px] hidden sm:flex"
              variant="outline"
              disabled={isCampaignsLoading}
            >
              {isCampaignsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <p className="text-ellipsis overflow-hidden whitespace-nowrap">
                    {campaignsFilter.length > 0
                      ? campaignsFilter.map((c) => c.name).join(', ')
                      : LL.salesPage.filters.campaigns()}
                  </p>
                  <ChevronDown />
                </>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0">
            <Command>
              <CommandInput
                placeholder={LL.salesPage.filters.searchCampaign()}
                className="h-9"
              />
              <CommandList>
                <CommandEmpty>{LL.salesPage.filters.noCampaign()}</CommandEmpty>
                <CommandGroup>
                  {campaigns?.map((campaign) => (
                    <CommandItem
                      className="cursor-pointer"
                      key={campaign.id}
                      onSelect={() => {
                        setCampaignsFilter((prev) => {
                          const exists = prev.some((c) => c.id === campaign.id);
                          if (exists)
                            return prev.filter((c) => c.id !== campaign.id);

                          return [
                            ...prev,
                            { id: campaign.id, name: campaign.name },
                          ];
                        });
                      }}
                    >
                      {campaign.name}
                      <Check
                        className={cn(
                          'ml-auto',
                          campaignsFilter.find((c) => c.id === campaign.id) !==
                            undefined
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex flex-row gap-2 items-start">
        <WithTooltip tooltip={LL.salesPage.filters.cleanAllFilters()}>
          <Button
            className="w-full max-w-[200px] justify-between"
            onClick={() => clearFilters()}
            variant="outline"
          >
            <FunnelX />
          </Button>
        </WithTooltip>

        <WithTooltip tooltip={LL.salesPage.table.refresh()}>
          <Button
            className="justify-between"
            onClick={() => refetch()}
            disabled={isLoading}
            variant="outline"
          >
            <RotateCcw className={isLoading ? 'animate-spin' : ''} />
          </Button>
        </WithTooltip>
      </div>
    </div>
  );
}
